/** The value actions of the support-ticket pages — one family, one links module (the detail-page
 *  value actions rule). Every href comes from `gridLinks`; `supportTicketLinks.test.ts` proves each
 *  one lands on a real route. Filter links write the grid's own URL params (`tenant`, `status`,
 *  `severity`, `booking`, `from` / `to`), so the toolbar shows the filter they set. */
import type { SupportTicketSeverity, SupportTicketStatus } from '@/api/supportTickets';
import {
  severityLabel,
  statusLabel,
  SUPPORT_TICKET_STATUS_AUDIT_EVENT,
} from '@/api/supportTickets';
import type { GridLink } from '@/components/ValueActions/identity';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { auditHref, supportTicketsHref } from '@/utils/gridLinks';

/** The ticket page — the backend's `console_path` (`/support-tickets/<public_id>`). */
export const supportTicketHref = (publicId: string) =>
  `/support-tickets/${encodeURIComponent(publicId)}`;

/** The console audit log's status changes (with their reasons) of a tenant's tickets: the audit
 *  rows carry the tenant (`tour_operator_id`), not a searchable ticket id. */
export const statusChangesAuditHref = (tenantId: number) =>
  auditHref({ event_contains: SUPPORT_TICKET_STATUS_AUDIT_EVENT, tenant: tenantId });

/** The operator part of a header: "Support tickets of this operator". */
export const tenantGrid = (tenantId: number): GridLink => ({
  href: supportTicketsHref({ tenant: tenantId }),
  label: 'Support tickets of this operator',
});

export function ticketActions(publicId: string): ValueAction[] {
  return [
    act.open(supportTicketHref(publicId), 'Open this ticket'),
    act.copy(publicId, 'Copy ticket id'),
  ];
}

/** A booking the ticket's case covers. */
export function bookingActions(bookingId: number): ValueAction[] {
  return [
    act.open(`/bookings/${bookingId}`, 'Open booking'),
    act.filter(supportTicketsHref({ booking: bookingId }), 'Tickets covering this booking'),
  ];
}

export function statusActions(status: SupportTicketStatus): ValueAction[] {
  return [
    act.filter(supportTicketsHref({ status }), `Only ${statusLabel(status).toLowerCase()} tickets`),
  ];
}

export function severityActions(severity: SupportTicketSeverity): ValueAction[] {
  return [
    act.filter(
      supportTicketsHref({ severity }),
      `Only ${severityLabel(severity).toLowerCase()} tickets`,
    ),
  ];
}

/** The user who escalated, through their AI client. */
export function requesterActions(user: { id: number; email: string }): ValueAction[] {
  return [
    act.open(`/users/${user.id}`, 'Open user'),
    act.filter(auditHref({ actor_email: user.email }), 'Audit entries by this user'),
    act.copy(user.email, 'Copy email'),
  ];
}

const DAY_MS = 24 * 3_600_000;

/** Other tickets opened around a moment (±1 day). */
export function momentActions(iso: string): ValueAction[] {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return [];
  return [
    act.filter(
      supportTicketsHref({
        from: new Date(t - DAY_MS).toISOString(),
        to: new Date(t + DAY_MS).toISOString(),
      }),
      'Tickets opened within ±1 day',
    ),
    act.copy(iso, 'Copy timestamp'),
  ];
}
